import { useEffect, useRef, useState } from 'react';
import { useStoreState } from 'hooks';
import { useLogin } from 'store/auth';
import { FailedInstance } from '../consistencyReportTypes';
import { fetchConsistencyFailedInstances } from './fetchConsistencyFailedInstances';

export const useConsistencyFailedInstances = (
  runStatusId: string | null,
  status?: string | null
) => {
  const token = useStoreState((state) => state.auth.token);
  const tenant = useStoreState((state) => state.auth.tenant);
  const cluster = useStoreState((state) => state.auth.cluster);
  const expiry = useStoreState((state) => state.auth.expiry);
  const login = useLogin();
  // useLogin returns a new function reference on every render. Updating the ref
  // in an effect (post-commit) is safer in concurrent mode than mutating it
  // during the render phase, which can run multiple times before committing.
  const loginRef = useRef(login);
  useEffect(() => {
    loginRef.current = login;
  }, [login]);

  const [instances, setInstances] = useState<FailedInstance[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Always clear stale data immediately so the previous run's columns are
    // never visible while a new fetch is in flight.
    setInstances([]);
    setError(null);

    if (!runStatusId || status !== 'SUCCESS') {
      return;
    }
    // Wait for auth to hydrate - firing the fetch with an empty token makes
    // the backend respond 401 with "Please send a bearer token..." which then
    // surfaces as a confusing error in the UI.
    if (!token) {
      return;
    }

    const controller = new AbortController();

    setLoading(true);
    setError(null);

    fetchConsistencyFailedInstances(
      runStatusId,
      { token, tenant, cluster, expiry },
      loginRef.current,
      controller.signal
    )
      .then((data) => {
        if (!controller.signal.aborted) {
          setInstances(data);
        }
      })
      .catch((err: unknown) => {
        if (!controller.signal.aborted) {
          setError(err instanceof Error ? err.message : String(err));
          setInstances([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [runStatusId, status, token, tenant, cluster, expiry]);

  return { instances, loading, error };
};
